import { Format, Output, resize } from "../utils/resizeImage";
import { getUser, supabase } from "../supabaseClient";
import exifr from "exifr";
import { sleep } from "../utils/sleep";
import { getBackend } from "../utils/getBackend";
import { getAccessTokenHeader } from "./helper/getAccessTokenHeader";
import axios from "axios";
import { SelectedFile } from "../types/PostSelection";
import {
  IMAGE_PIPELINE_VERSION,
  ImageDerivative,
  ImageDerivativeManifest,
  ImageDerivativeSize,
  isCurrentImageManifest,
} from "../shared/imageDerivatives";

// Type for media metadata with usage information from RPC
type MediaMetadataWithUsage = {
  id: number;
  created_at: string;
  updated_at: string | null;
  path: string | null;
  folder_path: string | null;
  metadata: any;
  user_id: string | null;
  is_used: boolean;
};

interface FileObject {
  name: string;
  bucket_id: string;
  owner: string;
  id: string;
  updated_at: string;
  created_at: string;
  last_accessed_at: string;
  metadata: Record<string, any>;
  filePath?: string;
  rating?: number;
  isUsed?: boolean;
  imageDerivatives?: ImageDerivativeManifest;
  locationHint?: {
    LatLng?: [number[] | string, number[] | string];
    GPSMapDatum?: string;
    GPSLatitudeRef?: string;
    GPSLongitudeRef?: string;
    City?: string;
    State?: string;
    Country?: string;
  };
}

type FileWithPath = {
  name: string;
  mine_type: string;
  filePath?: string;
  url?: string;
  imageDerivatives?: ImageDerivativeManifest;
};

export const MEDIA_SIZE = {
  thumbnail: "thumbnail",
  960: "960px",
  2400: "2400px",
  3600: "3600px",
  original: "original",
  video: "video",
};

const urlCache = new Map();

let fileObjectCache = new Map<string, FileObject[]>();

export function invalidateFileObjectCache() {
  fileObjectCache.clear();
}

export async function getSignedUrl(path: string): Promise<string> {
  if (urlCache.has(path)) {
    console.log("URL from cache", path, urlCache.get(path));
    return urlCache.get(path);
  }

  const { data } = await supabase.storage
    .from("content")
    .createSignedUrl(path, 3600);

  if (!data?.signedUrl) {
    throw new Error(`No signed URL found for path: ${path}`);
  }

  urlCache.set(path, data?.signedUrl);

  return data.signedUrl;
}

export async function getFileMetadata(
  path: string,
  filename: string,
): Promise<any> {
  const { data }: { data: FileObject[] | null } = await supabase.storage
    .from("content")
    .list(path, {
      search: filename,
      limit: 1,
    });

  return data?.[0]?.metadata;
}

export async function getImageDerivativeManifest(
  originalPath: string,
): Promise<ImageDerivativeManifest | undefined> {
  const { data, error } = await supabase
    .from("media_metadata")
    .select("metadata")
    .eq("path", originalPath)
    .maybeSingle();

  if (error) throw error;

  const metadata = data?.metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return undefined;
  }

  return isCurrentImageManifest(metadata.imageDerivatives)
    ? metadata.imageDerivatives
    : undefined;
}

export async function getPanoramaSourceManifests(
  generatedOriginalPaths: string[],
): Promise<ImageDerivativeManifest[]> {
  const paths = [...new Set(generatedOriginalPaths)];
  if (paths.length === 0) return [];

  const { data, error } = await supabase
    .from("media_metadata")
    .select("metadata")
    .in("path", paths);

  if (error) throw error;

  const sourcePaths = new Set<string>();
  for (const row of data ?? []) {
    const metadata = row.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      continue;
    }

    const panorama = metadata.panorama;
    if (!panorama || typeof panorama !== "object" || Array.isArray(panorama)) {
      continue;
    }

    if (typeof panorama.sourceOriginalPath === "string") {
      sourcePaths.add(panorama.sourceOriginalPath);
    }
  }

  const manifests = await Promise.all(
    [...sourcePaths].map(getImageDerivativeManifest),
  );
  return manifests.filter(
    (manifest): manifest is ImageDerivativeManifest => !!manifest,
  );
}

export async function getPanoramaGeneratedPaths(paths: string[]) {
  const uniquePaths = [...new Set(paths)];
  if (uniquePaths.length === 0) return new Set<string>();

  const { data, error } = await supabase
    .from("media_metadata")
    .select("path, metadata")
    .in("path", uniquePaths);

  if (error) throw error;

  return new Set(
    (data ?? []).flatMap((row) => {
      const metadata = row.metadata;
      if (
        row.path &&
        metadata &&
        typeof metadata === "object" &&
        !Array.isArray(metadata) &&
        metadata.panorama &&
        typeof metadata.panorama === "object"
      ) {
        return [row.path];
      }
      return [];
    }),
  );
}

/**
 * Get Files
 * @param page Page number (0-based)
 * @param limit Number of files per page
 * @param folderPath Filter files by folder path (empty string for root folder)
 * @returns
 */
export async function getFiles(page = 0, limit = 25, folderPath = "") {
  console.log(
    "GET FILES CALL",
    page,
    limit,
    folderPath,
    typeof folderPath,
    folderPath.length,
  );

  const originalPath = await getMediaPath(MEDIA_SIZE.original);
  const imagePath = await getMediaPath(MEDIA_SIZE.thumbnail);

  const { data: mediaMetadataWithUsage, error: metadataError } =
    await supabase.rpc("get_media_metadata_with_usage", {
      p_folder_path: folderPath,
      p_page: page,
      p_limit: limit,
    });

  console.log("MEDIA METADATA WITH USAGE", mediaMetadataWithUsage);

  if (metadataError) {
    throw metadataError;
  }

  let allFilesInFolder: FileObject[] = [];

  if (fileObjectCache.has(folderPath)) {
    allFilesInFolder = fileObjectCache.get(folderPath) ?? [];
  } else {
    let filesInFolderCount = 1000;
    let filesPage = 0;

    while (filesInFolderCount === 1000) {
      let { data: filesBatch, error: imageErrors } = await supabase.storage
        .from("content")
        .list(originalPath, {
          limit: 1000,
          offset: filesPage * 1000,
          sortBy: { column: "created_at", order: "desc" },
        });

      if (imageErrors) {
        throw imageErrors;
      }

      filesInFolderCount = (filesBatch ?? []).length;
      allFilesInFolder = [...allFilesInFolder, ...(filesBatch ?? [])];
      filesPage++;
    }
  }

  console.log("ALL FILES IN FOLDER", allFilesInFolder);

  if (!fileObjectCache.has(folderPath)) {
    fileObjectCache.set(folderPath, [...allFilesInFolder]);
  }

  const allContent = mediaMetadataWithUsage
    .filter((mediaMetadata) => {
      const metadata = mediaMetadata.metadata;
      return !(
        metadata &&
        typeof metadata === "object" &&
        !Array.isArray(metadata) &&
        metadata.panorama &&
        typeof metadata.panorama === "object"
      );
    })
    .map((mediaMetadata) => {
      const metadata = mediaMetadata.metadata as Record<string, unknown>;
      const imageDerivatives = isCurrentImageManifest(
        metadata?.imageDerivatives,
      )
        ? metadata.imageDerivatives
        : undefined;

      return {
        ...allFilesInFolder.find((f) => {
          return `${originalPath}/${f.name}` === mediaMetadata.path;
        }),
        imageDerivatives,
        filePath:
          imageDerivatives?.derivatives.thumbnail?.path ??
          `${imagePath}/${mediaMetadata.path?.split("/").pop()}`,
        rating: (mediaMetadata?.metadata as { Rating?: number })?.Rating,
        isUsed: mediaMetadata.is_used,
        locationHint: {
          LatLng: [
            (mediaMetadata?.metadata as { GPSLatitude?: number[] | string })
              ?.GPSLatitude,
            (
              mediaMetadata?.metadata as {
                GPSLongitude?: number[] | string;
              }
            )?.GPSLongitude,
          ],
          GPSMapDatum: (mediaMetadata?.metadata as { GPSMapDatum?: string })
            ?.GPSMapDatum,
          GPSLatitudeRef: (
            mediaMetadata?.metadata as { GPSLatitudeRef?: string }
          )?.GPSLatitudeRef,
          GPSLongitudeRef: (
            mediaMetadata?.metadata as { GPSLongitudeRef?: string }
          )?.GPSLongitudeRef,
          City: (mediaMetadata?.metadata as { City?: string })?.City,
          State: (mediaMetadata?.metadata as { State?: string })?.State,
          Country: (mediaMetadata?.metadata as { Country?: string })?.Country,
        },
      } as FileObject;
    })
    .filter((f) => f.name && f.metadata);

  const filesWithUrls = await addSignedUrls(allContent, MEDIA_SIZE.thumbnail);

  console.log("ALL FILES", filesWithUrls);

  return filesWithUrls.map((f, index) => ({
    id: index,
    _id: f.id,
    name: f.name,
    url: f.url,
    mine_type: f.metadata?.mimetype ?? "image/jpeg",
    rating: f.rating,
    isUsed: f.isUsed,
    imageDerivatives: f.imageDerivatives,
    locationHint: f.locationHint,
  }));
}

/**
 * Upload Files
 */
export async function uploadFile(file: File, folderPath: string = "") {
  if (file.type.startsWith("image/")) {
    const exif = await exifr.parse(file, {
      xmp: true,
      iptc: true,
      exif: true,
      gps: true,
    });

    console.log("EXTRACTED EXIF", exif);

    const originalFile = await uploadFileToStorage(
      MEDIA_SIZE.original,
      file,
      folderPath,
    );

    // Use the actual uploaded filename (which may be unique) for metadata
    await supabase.from("media_metadata").upsert({
      path: originalFile.filePath,
      folder_path: folderPath,
      metadata: JSON.parse(JSON.stringify(exif).replaceAll("\\u0000", "")),
    });

    const imageDerivatives = await processImageWithBackend(originalFile);
    const thumbnail = imageDerivatives.derivatives.thumbnail;
    if (!thumbnail) {
      throw new Error("Image processing did not return a thumbnail");
    }

    const image = {
      name: originalFile.name,
      mine_type: file.type,
      filePath: thumbnail.path,
      url: await getSignedUrl(thumbnail.path),
      imageDerivatives,
      rating: exif?.Rating,
    };

    return image;
  } else if (file.type.startsWith("video/")) {
    console.log("Uploading video", file);

    const uploadedVideoFile = await uploadFileToStorage(
      MEDIA_SIZE.video,
      file,
      folderPath,
    );

    const thumbnail: File = await new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const videoElem = document.createElement("video");
      videoElem.crossOrigin = "anonymous";

      canvas.style.position = "fixed";
      canvas.style.top = "0";
      canvas.style.zIndex = "-100";
      videoElem.style.position = "fixed";
      videoElem.style.zIndex = "-100";
      videoElem.style.top = "0";

      document.body.appendChild(videoElem);
      document.body.appendChild(canvas);

      videoElem.addEventListener("loadedmetadata", async function () {
        canvas.width = videoElem.videoWidth;
        canvas.height = videoElem.videoHeight;

        await sleep(4);

        //setTimeout(() => {
        canvas
          .getContext("2d")
          ?.drawImage(
            videoElem,
            0,
            0,
            videoElem.videoWidth,
            videoElem.videoHeight,
          );

        console.log("canvas write image");

        await sleep(4);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject("Error");
            const file = new File(
              [blob],
              uploadedVideoFile.name + ".video-thumbnail.jpg",
              {
                type: "image/jpeg",
              },
            );
            resolve(file);
          },
          `image/jpeg`,
          0.9,
        );
        //}, 3000);
      });

      videoElem.src = uploadedVideoFile.url!;
    });

    const image = await resizeAndUploadImage(
      240,
      MEDIA_SIZE.thumbnail,
      thumbnail,
      folderPath,
      false /* skipSignedUrl */,
    );

    return image;
  }
}

/**
 * Manual Upload Files - Upload pre-sized images without resizing
 */
export async function uploadManualFiles(
  files: {
    thumbnail: File[];
    small: File[];
    medium: File[];
    large: File[];
    original: File[];
  },
  folderPath: string = "",
) {
  const uploadedFiles = [];
  const derivatives: Partial<Record<ImageDerivativeSize, ImageDerivative>> = {};
  let originalMetadata: Record<string, unknown> | null = null;
  let uploadedOriginalPath: string | undefined;

  // Get paths for different sizes
  const pathThumbnail = await getMediaPath(MEDIA_SIZE.thumbnail);
  const path960 = await getMediaPath(MEDIA_SIZE["960"]);
  const path2400 = await getMediaPath(MEDIA_SIZE["2400"]);
  const path3600 = await getMediaPath(MEDIA_SIZE["3600"]);
  const pathOriginal = await getMediaPath(MEDIA_SIZE.original);

  // Process each set of files by size
  const sizeMap = {
    thumbnail: {
      files: files.thumbnail,
      path: pathThumbnail,
      size: MEDIA_SIZE.thumbnail,
    },
    small: { files: files.small, path: path960, size: MEDIA_SIZE["960"] },
    medium: { files: files.medium, path: path2400, size: MEDIA_SIZE["2400"] },
    large: { files: files.large, path: path3600, size: MEDIA_SIZE["3600"] },
    original: {
      files: files.original,
      path: pathOriginal,
      size: MEDIA_SIZE.original,
    },
  };

  // Determine unique filename once based on original file (or any file if no original)
  const firstOriginalFile =
    files.original[0] ||
    files.thumbnail[0] ||
    files.small[0] ||
    files.medium[0] ||
    files.large[0];
  if (!firstOriginalFile) {
    throw new Error("No files provided for upload");
  }

  const uniqueFileName = await generateUniqueFileName(
    firstOriginalFile.name,
    pathOriginal,
  );

  for (const [sizeName, { files: sizeFiles, size }] of Object.entries(
    sizeMap,
  )) {
    for (const file of sizeFiles) {
      try {
        // Extract EXIF from original files only
        let exif = null;
        if (sizeName === "original") {
          exif = await exifr.parse(file, {
            xmp: true,
            iptc: true,
            exif: true,
            gps: true,
          });
          originalMetadata = JSON.parse(
            JSON.stringify(exif || {}).replaceAll("\\u0000", ""),
          );
          console.log("EXTRACTED EXIF", exif);
        }

        // Upload the file with predetermined unique filename
        const uploadedFile = await uploadFileToStorage(
          size,
          file,
          folderPath,
          sizeName === "thumbnail" ? false : true, // Only get signed URL for thumbnails
          uniqueFileName,
        );

        if (sizeName === "original") {
          uploadedOriginalPath = uploadedFile.filePath;
        } else {
          const derivativeSize = {
            thumbnail: "thumbnail",
            small: "960px",
            medium: "2400px",
            large: "3600px",
          }[sizeName] as ImageDerivativeSize;
          const dimensions = await getBrowserImageDimensions(file);
          derivatives[derivativeSize] = {
            path: uploadedFile.filePath!,
            mediaType: file.type,
            width: dimensions.width,
            height: dimensions.height,
            hdr: false,
          };
        }

        uploadedFiles.push({
          ...uploadedFile,
          rating: exif?.Rating,
          size: sizeName,
        });
      } catch (error) {
        console.error(`Error uploading ${sizeName} file ${file.name}:`, error);
        throw error;
      }
    }
  }

  const imageDerivatives: ImageDerivativeManifest = {
    pipelineVersion: IMAGE_PIPELINE_VERSION,
    sourceKind: "sdr",
    originalPath: uploadedOriginalPath || `${pathOriginal}/${uniqueFileName}`,
    derivatives,
  };

  if (uploadedOriginalPath) {
    const { error } = await supabase.from("media_metadata").upsert({
      path: uploadedOriginalPath,
      folder_path: folderPath,
      metadata: {
        ...(originalMetadata || {}),
        imageDerivatives,
      },
    });
    if (error) throw error;
  }

  const thumbnailFile =
    uploadedFiles.find((f) => f.size === "thumbnail") || uploadedFiles[0];
  return { ...thumbnailFile, imageDerivatives };
}

async function getBrowserImageDimensions(file: File) {
  try {
    const bitmap = await createImageBitmap(file);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
  } catch {
    const url = URL.createObjectURL(file);
    try {
      return await new Promise<{ width: number; height: number }>(
        (resolve, reject) => {
          const image = new Image();
          image.onload = () =>
            resolve({
              width: image.naturalWidth,
              height: image.naturalHeight,
            });
          image.onerror = () =>
            reject(new Error(`Unable to read dimensions for ${file.name}`));
          image.src = url;
        },
      );
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

async function processImageWithBackend(
  originalFile: FileWithPath,
): Promise<ImageDerivativeManifest> {
  const user = await getUser();
  if (!user || !originalFile.filePath) {
    throw new Error("Image processing requires an authenticated user");
  }

  const { data } = await axios.post<ImageDerivativeManifest>(
    getBackend() + "/tools/images/process",
    {
      userId: user.id,
      originalPath: originalFile.filePath,
    },
    {
      headers: await getAccessTokenHeader(),
      timeout: 1000 * 60 * 5,
    },
  );

  return data;
}

async function generateUniqueFileName(
  originalFileName: string,
  path: string,
): Promise<string> {
  // Check if file exists
  const { data: existingFiles } = await supabase.storage
    .from("content")
    .list(path, {
      search: originalFileName,
      limit: 1,
    });

  // If no file exists with this name, return original name
  if (!existingFiles || existingFiles.length === 0) {
    return originalFileName;
  }

  // Generate unique name with timestamp
  const timestamp = Date.now();
  const fileExtension = originalFileName.substring(
    originalFileName.lastIndexOf("."),
  );
  const fileNameWithoutExtension = originalFileName.substring(
    0,
    originalFileName.lastIndexOf("."),
  );

  return `${fileNameWithoutExtension}_${timestamp}${fileExtension}`;
}

async function resizeAndUploadImage(
  maxSize: number,
  pathSize: string,
  file: File,
  folderPath: string,
  skipSignedUrl = true,
) {
  const newFile = await resize(file, {
    maxWidth: maxSize,
    maxHeight: maxSize,
    quality: 0.9,
    output: Output.File,
    format: Format.jpeg,
  });
  return uploadFileToStorage(pathSize, newFile, folderPath, skipSignedUrl);
}

async function uploadFileToStorage(
  size: string,
  file: File,
  folderPath: string,
  skipSignedUrl = false,
  predeterminedFileName?: string,
) {
  const path = await getMediaPath(size);

  // Use predetermined filename or generate unique filename if needed
  const uniqueFileName =
    predeterminedFileName ?? (await generateUniqueFileName(file.name, path));
  const filePath = `${path}/${uniqueFileName}`;

  // Create a new File object with the unique name if name changed
  let fileToUpload = file;
  if (uniqueFileName !== file.name) {
    fileToUpload = new File([file], uniqueFileName, { type: file.type });
  }

  const newFile: FileWithPath = {
    name: uniqueFileName,
    mine_type: file.type,
  };

  const { data, error } = await supabase.storage
    .from("content")
    .upload(filePath, fileToUpload, {
      cacheControl: "3600",
      upsert: false, // Changed from upsert: true to false to prevent overwriting
    });

  if (error) {
    throw error;
  }

  const { data: fileObject } = await supabase.storage
    .from("content")
    .list(path, {
      search: uniqueFileName,
      limit: 1,
    });

  if (size === MEDIA_SIZE.original) {
    if (fileObjectCache.has(folderPath)) {
      const existingFiles = fileObjectCache.get(folderPath) ?? [];
      fileObjectCache.set(folderPath, [...existingFiles, fileObject![0]]);
    } else {
      fileObjectCache.set(folderPath, [fileObject![0]]);
    }
  }

  newFile.filePath = data.path;

  if (!skipSignedUrl) {
    newFile.url = await getSignedUrl(filePath);
  }

  return newFile;
}

async function addSignedUrls(files: FileObject[], size: string) {
  if (!files.length) return [];

  const urls = files.map((f) => f.filePath!);

  const uncachedUrls = urls.filter((url) => !urlCache.has(url));

  if (uncachedUrls.length > 0) {
    const { data, error } = await supabase.storage
      .from("content")
      .createSignedUrls(uncachedUrls, 3600);

    if (error) {
      throw error;
    }

    for (const item of data) {
      urlCache.set(item.path, item.signedUrl);
    }
  }

  return files.map((f) => ({
    ...f,
    url: urlCache.get(f.filePath!),
    filePath: f.name,
  }));
}

export async function getMediaPath(size = MEDIA_SIZE.original) {
  const user = await getUser();
  return `${user?.id}/media/${size}`;
}

export async function getMediaPathWithFilename(size: string, name: string) {
  return (await getMediaPath(size)) + "/" + name;
}

/**
 * Get media metadata with usage information using RPC
 * @param folderPath Filter files by folder path (empty string for root folder)
 * @param page Page number (0-based)
 * @param limit Number of files per page
 * @returns Promise containing media metadata with is_used field
 */
export async function getMediaMetadataWithUsage(
  folderPath = "",
  page = 0,
  limit = 25,
): Promise<MediaMetadataWithUsage[]> {
  const { data, error } = await supabase.rpc("get_media_metadata_with_usage", {
    p_folder_path: folderPath,
    p_page: page,
    p_limit: limit,
  });

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Delete selected files from storage and metadata
 * Only deletes files that are not used in posts
 * @param selectedFiles Array of selected files to delete
 * @param currentFolder Current folder path for cache invalidation
 * @returns Promise containing count of deleted files
 */
export async function deleteSelectedFiles(
  selectedFiles: SelectedFile[],
  currentFolder: string = "",
): Promise<number> {
  let deletedCount = 0;

  for (const file of selectedFiles) {
    // Only delete if not used in posts
    if (!file.isUsed) {
      try {
        // Get all file sizes/versions for this file
        const originalPath = await getMediaPath(MEDIA_SIZE.original);
        const thumbnailPath = await getMediaPath(MEDIA_SIZE.thumbnail);
        const path960 = await getMediaPath(MEDIA_SIZE[960]);
        const path2400 = await getMediaPath(MEDIA_SIZE[2400]);
        const path3600 = await getMediaPath(MEDIA_SIZE[3600]);
        const videoPath = await getMediaPath(MEDIA_SIZE.video);

        const isVideo = file.mine_type?.startsWith("video/");

        let filePaths: string[] = [];

        if (isVideo) {
          // For videos, we have the original video and a thumbnail with .video-thumbnail.jpg suffix
          filePaths = [
            `${videoPath}/${file.name}`,
            `${thumbnailPath}/${file.name}.video-thumbnail.jpg`,
          ];
        } else {
          filePaths = file.imageDerivatives
            ? [
                file.imageDerivatives.originalPath,
                ...Object.values(file.imageDerivatives.derivatives)
                  .filter(
                    (derivative): derivative is ImageDerivative => !!derivative,
                  )
                  .map((derivative) => derivative.path),
              ]
            : [
                `${originalPath}/${file.name}`,
                `${thumbnailPath}/${file.name}`,
                `${path960}/${file.name}`,
                `${path2400}/${file.name}`,
                `${path3600}/${file.name}`,
              ];
        }

        // Delete all versions from storage
        const { error: storageError } = await supabase.storage
          .from("content")
          .remove(filePaths);

        if (storageError) {
          console.warn(
            `Failed to delete storage files for ${file.name}:`,
            storageError,
          );
        }

        // Delete metadata entry (path is always the original path)
        const metadataPath = isVideo
          ? `${videoPath}/${file.name}`
          : file.imageDerivatives?.originalPath ||
            `${originalPath}/${file.name}`;
        const { error: metadataError } = await supabase
          .from("media_metadata")
          .delete()
          .eq("path", metadataPath);

        if (metadataError) {
          console.warn(
            `Failed to delete metadata for ${file.name}:`,
            metadataError,
          );
        } else {
          deletedCount++;
        }

        // Clear cache for this file
        filePaths.forEach((path) => {
          if (urlCache.has(path)) {
            urlCache.delete(path);
          }
        });
      } catch (error) {
        console.warn(`Error deleting file ${file.name}:`, error);
      }
    }
  }

  // Clear file cache for the current folder
  if (fileObjectCache.has(currentFolder)) {
    fileObjectCache.delete(currentFolder);
  }

  return deletedCount;
}
